import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, Text, Textarea, Input } from "@chakra-ui/react";
import AttachmentStrip from "./AttachmentStrip";
import SubmittedAnswer from "./SubmittedAnswer";
import AnswerActionBar from "./AnswerActionBar";
import AnswerCanvas from "./AnswerCanvas";
import FormulaInput from "./FormulaInput";
import { useAnswerDraft } from "./useAnswerDraft";
import { useDictation } from "./useDictation";
import { ANSWER_MODES } from "./answerModes";
import { a } from "../appColors";

const CANVAS_MIN_HEIGHT = 340;

/**
 * The drawing, flattened into one picture.
 *
 * The marker receives images, not a stroke list, so the canvas is rasterised
 * at submit time. The figures were loaded with crossOrigin set, so the canvas
 * is not tainted and this does not throw.
 */
const flattenCanvas = (stage) => {
  if (!stage) return null;
  try {
    return stage.toDataURL({ pixelRatio: 2 });
  } catch (err) {
    console.error("Could not capture the drawing:", err);
    return null;
  }
};

/**
 * Where a student writes one answer (Figma 194:67, 215:187, 222:475).
 *
 * The box has two faces over one draft: typing and drawing. Switching does
 * not move the answer anywhere — the text, the pictures and the ink are all
 * held by `useAnswerDraft` and only the face changes, so a student can write
 * a line, draw a diagram beside it and go back to writing without losing
 * either.
 *
 * Everything below is presentational and memoised. The box owns no answer
 * data of its own; it wires the draft to the pieces and reports a submission
 * upward.
 */
const AnswerBox = forwardRef(({
  questionGuid,
  figures = [],
  initialText = "",
  initialImages = [],
  readOnly = false,
  isDark = false,
  isSubmitting = false,
  onSubmit,
  onUploadImage,
  onInsertFormula,
  /** Told whenever the answer changes, so the page can keep its own copy for
   *  a whole-paper submit without this box reaching into it. */
  onChange,
  /** Shown under a handed-in answer: the marker's verdict, if there is one. */
  feedback,
  /** The pen the toolbar is set to. */
  penColour,
}, ref) => {
  const draft = useAnswerDraft({ initialText, initialImages });

  /**
   * An answer already handed in is shown, not re-opened.
   *
   * Coming back to a paper should look like coming back to your own work.
   * Editing is a deliberate step from there, and submitting again returns
   * to this view, so the box always says what the server has.
   */
  const hasSavedAnswer = Boolean(initialText?.trim() || initialImages.length);
  const [isEditing, setIsEditing] = useState(!hasSavedAnswer);

  // What the answer looked like when editing began, so changing your mind
  // costs nothing. Without it the only way out of an edit was to submit.
  const beforeEdit = useRef(null);

  /**
   * The pictures that actually went with the answer.
   *
   * Not the draft's attachments: a figure dropped in to be drawn on is
   * folded into the flattened drawing and left out of the submission, so
   * showing the draft afterwards showed a clean diagram the marker never
   * saw — and an answer that was only ink showed nothing at all.
   */
  const [submittedImages, setSubmittedImages] = useState(() =>
    initialImages.map((img) =>
      typeof img === "string" ? { url: img, name: img } : img
    )
  );
  const startEditing = useCallback(() => {
    beforeEdit.current = {
      text: draft.text,
      attachments: draft.attachments,
      strokes: draft.strokes,
    };
    setIsEditing(true);
  }, [draft.text, draft.attachments, draft.strokes]);

  const cancelEditing = useCallback(() => {
    if (beforeEdit.current) draft.reset(beforeEdit.current);
    setIsEditing(false);
  }, [draft]);
  const isPenMode = draft.mode === ANSWER_MODES.PEN;
  const fileInputRef = useRef(null);
  const surfaceRef = useRef(null);
  const stageRef = useRef(null);

  /**
   * Konva needs pixels, so the box is measured rather than assumed.
   *
   * Measured through a callback ref: an answer already handed in renders the
   * submitted view instead of this box, so an effect reading the ref on
   * mount found nothing and never looked again. Pressing Edit then gave a
   * canvas zero pixels wide, which silently swallowed every stroke.
   */
  const [canvasWidth, setCanvasWidth] = useState(0);
  const sizeObserver = useRef(null);
  const measureSurface = useCallback((node) => {
    sizeObserver.current?.disconnect();
    surfaceRef.current = node;
    if (!node) return;
    setCanvasWidth(Math.floor(node.getBoundingClientRect().width));
    const observer = new ResizeObserver(([entry]) =>
      setCanvasWidth(Math.floor(entry.contentRect.width))
    );
    observer.observe(node);
    sizeObserver.current = observer;
  }, []);
  useEffect(() => () => sizeObserver.current?.disconnect(), []);

  /**
   * What the page may do to this box from outside.
   *
   * Deliberately narrow: a drawing pushed over from the question needs to
   * land here, but nothing outside should be able to rewrite the answer.
   */
  useImperativeHandle(
    ref,
    () => ({
      addImage: (attachment) => draft.addAttachment(attachment),
      removeImageNamed: (name) => draft.removeAttachmentNamed(name),
      clearInk: () => draft.setStrokes([]),
    }),
    [draft]
  );

  /**
   * Dictation appends whole phrases as the recogniser settles on them.
   *
   * A space is added only where one is needed, so speaking three sentences
   * reads as a paragraph rather than a run-on.
   */
  const dictation = useDictation({
    onCommit: (phrase) =>
      draft.setText((prev) => {
        const clean = phrase.trim();
        if (!clean) return prev;
        if (!prev) return clean.charAt(0).toUpperCase() + clean.slice(1);
        return /\s$/.test(prev) ? prev + clean : `${prev} ${clean}`;
      }),
  });

  // The pen has no text to dictate into.
  useEffect(() => {
    if (isPenMode && dictation.listening) dictation.stop();
  });

  /**
   * Undo reaches the ink in an answer too.
   *
   * Listened for on the way down rather than on the way up, and flagged so
   * the board's own handler leaves it alone: the shortcut has to mean the
   * thing being drawn on, and while a pen is out in an answer box that is
   * this canvas, not the board behind it.
   */
  useEffect(() => {
    if (!isPenMode || readOnly) return undefined;
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z" || e.shiftKey) return;
      if (!draft.strokes.length) return;
      e.preventDefault();
      e.__standeUndoHandled = true;
      draft.undoStroke();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isPenMode, readOnly, draft]);

  const handlePickFile = useCallback(() => fileInputRef.current?.click(), []);

  /**
   * Formulas are written in a MathLive field and dropped into the text as
   * `$$latex$$` — the form answers have always reached the marker in.
   *
   * A page can still take this over through `onInsertFormula`; otherwise the
   * box handles it itself. Formulas live in the text, so asking for one from
   * the pen face switches back to typing first.
   */
  const [showFormula, setShowFormula] = useState(false);

  const handleOpenFormula = useCallback(() => {
    if (onInsertFormula) {
      onInsertFormula();
      return;
    }
    if (draft.mode !== ANSWER_MODES.TYPE) draft.setMode(ANSWER_MODES.TYPE);
    setShowFormula(true);
  }, [onInsertFormula, draft]);

  const handleInsertFormula = useCallback(
    (latex) =>
      draft.setText((prev) => {
        const block = `$$${latex}$$`;
        if (!prev) return block;
        return /\s$/.test(prev) ? prev + block : `${prev} ${block}`;
      }),
    [draft]
  );

  const closeFormula = useCallback(() => setShowFormula(false), []);

  const handleFileChosen = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      event.target.value = ""; // so choosing the same file twice still fires
      if (!file) return;
      if (onUploadImage) {
        onUploadImage(file, draft.addAttachment);
        return;
      }
      const url = URL.createObjectURL(file);
      draft.addAttachment({ kind: "file", file, url, name: file.name });
    },
    [draft, onUploadImage]
  );

  /**
   * The pictures in this answer, as the submit path wants them.
   *
   * Anything the student attached keeps its own url; the drawing, if there is
   * one, is added as one more picture. Figures that were only dropped in to
   * be drawn on are left out — they are already inside that flattened image,
   * and sending both would mark the same diagram twice.
   */
  const collectImages = useCallback(() => {
    const drawing = draft.strokes.length ? flattenCanvas(stageRef.current) : null;
    const attached = draft.attachments
      .filter((att) => (drawing ? att.kind !== "figure" : true))
      .map((att, i) => ({
        name: att.name || att.label || `attachment-${i + 1}.png`,
        url: att.url || att.image?.src,
      }))
      .filter((img) => img.url);
    return drawing ? [...attached, { name: "annotation", url: drawing }] : attached;
  }, [draft.attachments, draft.strokes]);

  const handleSubmit = useCallback(() => {
    const images = collectImages();
    setSubmittedImages(images);
    onSubmit?.(questionGuid, draft.text, images);
    draft.markSubmitted({ text: draft.text, count: draft.attachments.length });
    setIsEditing(false);
  }, [collectImages, draft, onSubmit, questionGuid]);

  // Reported after the change lands, not during it, so the page's state
  // update never happens inside this component's render.
  //
  // Debounced because collecting the images rasterises the canvas: a run of
  // quick strokes would otherwise redraw the whole thing once per stroke,
  // and only the last result is ever used.
  useEffect(() => {
    if (!onChange) return undefined;
    const t = setTimeout(
      () => onChange(questionGuid, { text: draft.text, images: collectImages() }),
      300
    );
    return () => clearTimeout(t);
  }, [draft.text, draft.attachments, draft.strokes, collectImages, onChange, questionGuid]);



  // Review mode never edits; a student with an answer sees it until they
  // choose to change it.
  if (readOnly || !isEditing) {
    return (
      <SubmittedAnswer
        text={draft.text}
        attachments={submittedImages}
        isMarking={isSubmitting}
        onEdit={readOnly ? undefined : startEditing}
      >
        {feedback}
      </SubmittedAnswer>
    );
  }

  return (
    <Box
      ref={measureSurface}
      position="relative"
      bg={a.surface}
      border="1px solid"
      borderColor={a.edge}
      borderRadius="14px"
      overflow="hidden"
      width="100%"
    >
      {/* Pictures ride above both faces: they belong to the answer, not to
          the mode that happened to add them. */}
      {!isPenMode && (
        <AttachmentStrip
          attachments={draft.attachments}
          readOnly={readOnly}
          onRemove={draft.removeAttachment}
          onOpen={() => draft.setMode(ANSWER_MODES.PEN)}
        />
      )}

      {isPenMode ? (
        <AnswerCanvas
          width={canvasWidth}
          height={CANVAS_MIN_HEIGHT}
          attachments={draft.attachments}
          strokes={draft.strokes}
          ref={stageRef}
          onAddStroke={draft.addStroke}
          onMoveAttachment={draft.moveAttachment}
          readOnly={readOnly}
          isDark={isDark}
          colour={penColour}
        />
      ) : (
        <Textarea
          // While listening, the recogniser owns the field: the working guess
          // is shown after the settled text, and typing is held off so the
          // two cannot fight over the caret.
          value={
            dictation.listening && dictation.interim
              ? `${draft.text}${draft.text && !/\s$/.test(draft.text) ? " " : ""}${dictation.interim}`
              : draft.text
          }
          onChange={(e) => draft.setText(e.target.value)}
          isReadOnly={readOnly || dictation.listening}
          placeholder="Type  your answer here .... "
          variant="unstyled"
          resize="none"
          rows={3}
          px="19px"
          pt={draft.attachments.length ? "10px" : "19px"}
          minH="92px"
          fontSize="13px"
          color={a.text}
          // The design's #4b4b4b is a light-mode grey; on a near-black card
          // it is all but invisible.
          // The placeholder is the only italic here; what a student writes
          // is their answer, not an aside.
          _placeholder={{ color: a.textMuted, fontSize: "13px", fontStyle: "italic" }}
        />
      )}

      {showFormula && !isPenMode && (
        <FormulaInput
          isDark={isDark}
          onInsert={handleInsertFormula}
          onClose={closeFormula}
        />
      )}

      <AnswerActionBar
        mode={draft.mode}
        onToggleMode={draft.toggleMode}
        figures={figures}
        addedFigureIds={draft.figureIds}
        onUpload={handlePickFile}
        onInsertFormula={handleOpenFormula}
        onInsertFigure={draft.insertFigure}
        onSubmit={handleSubmit}
        canSubmit={!draft.isEmpty}
        isSubmitting={isSubmitting}
        onCancel={hasSavedAnswer && beforeEdit.current ? cancelEditing : undefined}
        onDictate={dictation.supported ? dictation.toggle : undefined}
        isDictating={dictation.listening}
        dictationSupported={dictation.supported}
        readOnly={readOnly}
        isDark={isDark}
      />

      {dictation.error && (
        <Text
          role="status"
          px="19px"
          pb="10px"
          mt="-6px"
          fontSize="11px"
          lineHeight="15px"
          color={a.badText}
        >
          {dictation.error}
        </Text>
      )}

      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        display="none"
        onChange={handleFileChosen}
      />
    </Box>
  );
});
AnswerBox.displayName = "AnswerBox";

export default memo(AnswerBox);
